import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { VisualSplitService } from '../../services/visual-split.service';
import { ProgressUpdate, VisualSplitError } from '../../models/visual-split.model';
import { ServiceIcon } from '../shared/page-header/page-header.component';
import { ToastService } from '../../services/toast.service';
import { PageHeaderComponent } from '../shared/page-header/page-header.component';
import { ConfigPanelComponent } from '../shared/config-panel/config-panel.component';
import { VideoSettingsCameraOnlyComponent } from '../shared/video-settings-camera-only/video-settings-camera-only.component';
import { VideoSettingsCameraOnly } from '../../models/video-settings.model';
import { I18nService } from '../../i18n/i18n.service';

@Component({
  selector: 'app-key-visual-splitter',
  standalone: true,
  imports: [CommonModule, FormsModule, PageHeaderComponent, ConfigPanelComponent, VideoSettingsCameraOnlyComponent],
  templateUrl: './key-visual-splitter.component.html',
  styleUrls: ['./key-visual-splitter.component.scss']
})
export class KeyVisualSplitterComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  // ヘッダーアイコン設定
  headerIcons: ServiceIcon[] = [
    { src: '/assets/icon_firefly.svg', alt: 'Firefly' },
    { src: '/assets/icon_google_drive.png', alt: 'Google Drive' }
  ];

  // アップロードされた画像データ
  uploadedFile: File | null = null;

  // 分割数（デフォルト: 20）
  splitCount: number = 20;

  // 動画生成用プロンプト
  videoPrompt: string = '';

  // 動画設定
  selectedVideoSize: string = '540p';
  videoSettings: VideoSettingsCameraOnly = {
    cameraMotion: ''
  };

  // 処理状態
  isProcessing: boolean = false;

  // 進行状況
  progressSteps = [
    { name: '動画生成', status: 'pending', percentage: 0 },
    { name: '動画分割', status: 'pending', percentage: 0 }
  ];

  // 現在のステップ
  currentStep: number = -1;

  // 分割結果の画像
  resultImages: string[] = [];

  // 生成された動画のURL
  generatedVideoUrl: string | null = null;

  // 再分割用の分割数
  newSplitCount: number = 20;

  // 分割処理中フラグ
  isSplitting: boolean = false;

  // エラーメッセージ
  errorMessage: string = '';

  constructor(
    private visualSplitService: VisualSplitService,
    private toastService: ToastService,
    public i18n: I18nService
  ) { }

  get imageUploadConfig() {
    return {
      label: this.i18n.t('kvs.uploadLabel'),
      placeholder: this.i18n.t('kvs.uploadPlaceholder'),
      hint: this.i18n.t('kvs.uploadHint'),
      acceptTypes: 'image/*',
      maxSize: 10
    };
  }

  get promptConfig() {
    return {
      label: this.i18n.t('kvs.promptLabel'),
      placeholder: this.i18n.t('kvs.promptPlaceholder'),
      rows: 4
    };
  }

  get buttonConfig() {
    return {
      defaultText: this.i18n.t('kvs.generateBtn'),
      processingText: this.i18n.t('kvs.generatingBtn')
    };
  }

  ngOnInit(): void {
    // 進行状況の監視
    this.visualSplitService.progress$
      .pipe(takeUntil(this.destroy$))
      .subscribe((progress: ProgressUpdate) => {
        this.progressSteps = progress.steps;
        this.currentStep = progress.currentStep;

        if (progress.isComplete && progress.result) {
          this.resultImages = progress.result.images;
          this.generatedVideoUrl = progress.result.videoUrl || null;
          this.newSplitCount = this.splitCount; // 現在の分割数を新しい分割数として設定
          this.isProcessing = false;
        }
      });

    // エラーの監視
    this.visualSplitService.error$
      .pipe(takeUntil(this.destroy$))
      .subscribe((error: VisualSplitError) => {
        this.errorMessage = error.message;
        this.isProcessing = false;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ConfigPanelComponentからのファイル選択
  handleFileSelected(file: File): void {
    this.uploadedFile = file;
  }

  // ConfigPanelComponentからのプロンプト変更
  handlePromptChanged(prompt: string): void {
    this.videoPrompt = prompt;
  }

  // ConfigPanelComponentからのバリデーションエラー
  handleValidationError(message: string): void {
    this.toastService.showToast(message, "error");
  }

  // 生成開始
  startGeneration(): void {
    if (this.uploadedFile && this.videoPrompt.trim()) {
      this.isProcessing = true;
      this.errorMessage = '';
      this.resultImages = [];
      this.generatedVideoUrl = null;

      this.visualSplitService.processImageToSplitFrames(
        this.uploadedFile,
        this.videoPrompt,
        this.splitCount,
        {
          selectedSize: this.selectedVideoSize,
          cameraMotion: this.videoSettings.cameraMotion,
          promptStyle: '',
          shotAngle: '',
          shotSize: ''
        }
      ).subscribe({
        next: (result) => {
          console.log('生成完了:', result);
        },
        error: (error) => {
          console.error('生成エラー:', error);
          this.isProcessing = false;
        }
      });
    }
  }

  // 動画の再分割
  resplitVideo(): void {
    if (this.generatedVideoUrl && this.newSplitCount !== this.splitCount) {
      this.isSplitting = true;

      this.visualSplitService.splitVideoOnly(this.generatedVideoUrl, this.newSplitCount).subscribe({
        next: (splitResult) => {
          this.resultImages = splitResult.data?.images || [];
          this.splitCount = this.newSplitCount; // 現在の分割数を更新
          this.isSplitting = false;
          console.log('再分割完了:', splitResult);
        },
        error: (error) => {
          console.error('再分割エラー:', error);
          this.isSplitting = false;
          this.errorMessage = this.i18n.t('kvs.resplitFailed');
        }
      });
    }
  }

  // 動画設定の変更ハンドラー
  onVideoSettingsChange(settings: VideoSettingsCameraOnly): void {
    this.videoSettings = settings;
  }

  // リセット（完全にクリア）
  reset(): void {
    this.uploadedFile = null;
    this.splitCount = 20;
    this.videoPrompt = '';
    this.selectedVideoSize = '540p';
    this.videoSettings = {
      cameraMotion: ''
    };
    this.isProcessing = false;
    this.progressSteps = [
      { name: '動画生成', status: 'pending', percentage: 0 },
      { name: '動画分割', status: 'pending', percentage: 0 }
    ];
    this.currentStep = -1;
    this.resultImages = [];
    this.generatedVideoUrl = null;
    this.newSplitCount = 20;
    this.isSplitting = false;
    this.errorMessage = '';
  }
}